import { useMemo } from 'react';
import { useStore } from '@/providers/StoreProvider';
import { createPriceRangeOptions } from '@repo/utils';
import { useAnalytics } from '@/hooks/useAnalytics';

// Extended option type that includes null for UI
interface ExtendedPriceOption {
  value: number | null;
  label: string;
}

// Shared hook for both variants - exported for reuse
export function usePriceFilter() {
  const analytics = useAnalytics();
  const price = useStore(s => s.listingsFilters.price);
  const setPrice = useStore(s => s.listingsFilters.setPrice);

  const minOptions = useMemo(
    (): ExtendedPriceOption[] => [
      { value: null, label: 'No min' },
      ...createPriceRangeOptions().map(option => ({
        value: option.value,
        label: option.label,
      })),
    ],
    [],
  );

  const maxOptions = useMemo(
    (): ExtendedPriceOption[] => [
      { value: null, label: 'No max' },
      ...createPriceRangeOptions().map(option => ({
        value: option.value,
        label: option.label,
      })),
    ],
    [],
  );

  const handleMinChange = (v: number | null) => {
    analytics.track('listings_filter_price_min', { price: v });
    setPrice({
      ...price,
      min: v,
      // Auto-adjust max if min > max (only when both are numbers)
      ...(v !== null && price.max !== null && v > price.max ? { max: v } : {}),
    });
    if (v !== null && price.max !== null && v > price.max) {
      analytics.track('listings_filter_price_max', { price: v });
    }
  };

  const handleMaxChange = (v: number | null) => {
    analytics.track('listings_filter_price_max', { price: v });
    setPrice({
      ...price,
      max: v,
      // Auto-adjust min if max < min (only when both are numbers)
      ...(v !== null && price.min !== null && v < price.min ? { min: v } : {}),
    });
    if (v !== null && price.min !== null && v < price.min) {
      analytics.track('listings_filter_price_min', { price: v });
    }
  };

  const formatPriceDisplay = () => {
    const hasMin = price.min !== null;
    const hasMax = price.max !== null;

    if (!hasMin && !hasMax) {
      return undefined; // Show placeholder
    }

    if (hasMin && hasMax) {
      return `$${price.min! / 1000}k - $${price.max! / 1000}k`;
    }

    if (hasMin) {
      return `Min $${price.min! / 1000}k`;
    }

    if (hasMax) {
      return `Max $${price.max! / 1000}k`;
    }
  };

  return {
    price,
    minOptions,
    maxOptions,
    handleMinChange,
    handleMaxChange,
    formatPriceDisplay,
  };
}
